import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { navItems } from "@/content";

const siteUrl = "https://pawansharma.co";

export function PageMeta({ title, description, path = "" }: { title: string; description: string; path?: string }) {
  useEffect(() => {
    document.title = title;
    const setMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property=\"${name}\"]` : `meta[name=\"${name}\"]`;
      let element = document.head.querySelector(selector) as HTMLMetaElement | null;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(property ? "property" : "name", name);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", `${siteUrl}${path}`, true);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    let canonical = document.head.querySelector("link[rel=canonical]") as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}${path}`;
  }, [title, description, path]);
  return null;
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();
  const homeAtTop = location === "/" && !scrolled;

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 28);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`site-header ${homeAtTop ? "site-header--dark" : "site-header--paper"}`}>
      <div className="header-inner">
        <Link href="/" className="wordmark" aria-label="Pawan Sharma, home">
          <span>Pawan</span> Sharma
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navItems.map(([label, href]) => (
            <Link key={href} href={href} className={location === href ? "active" : ""}>
              {label}
            </Link>
          ))}
        </nav>
        <button className="menu-button" aria-expanded={open} aria-controls="mobile-menu" onClick={() => setOpen(!open)}>
          <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <nav id="mobile-menu" className={`mobile-nav ${open ? "is-open" : ""}`} aria-label="Mobile navigation">
        {navItems.map(([label, href], index) => (
          <Link key={href} href={href} className={location === href ? "active" : ""}>
            <span>{String(index + 1).padStart(2, "0")}</span>{label}
          </Link>
        ))}
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-frame">
        <div>
          <p className="eyebrow light">Pawan Sharma</p>
          <h2>Film, research, writing,<br />and public work.</h2>
        </div>
        <div className="footer-contact">
          <p>Write to me about a research question, a film or media project, an editorial need, a course or workshop, or a public program that could benefit from my experience.</p>
          <a href="mailto:sharm684@umn.edu" className="text-link light-link">Email Pawan <ArrowUpRight size={16} /></a>
        </div>
      </div>
      <div className="footer-base">
        <span>© {new Date().getFullYear()} Pawan Sharma</span>
        <div>
          <a href="https://www.instagram.com/roadnama/" target="_blank" rel="noreferrer">Instagram</a>
          <a href="mailto:sharm684@umn.edu">Email</a>
        </div>
        <span>Minneapolis–Saint Paul · Malwa</span>
      </div>
    </footer>
  );
}

export default function SiteLayout({ children }: { children: ReactNode }) {
  const [location] = useLocation();

  useEffect(() => {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      elements.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")),
      { threshold: 0.12, rootMargin: "0px 0px -40px" },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [location]);

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Header />
      <main id="main-content">{children}</main>
      <Footer />
    </>
  );
}

export function ArrowLink({ href, children, external = false, className = "" }: { href: string; children: ReactNode; external?: boolean; className?: string }) {
  if (external) {
    return <a href={href} target="_blank" rel="noreferrer" className={`text-link ${className}`}>{children}<ArrowUpRight size={16} /></a>;
  }
  return <Link href={href} className={`text-link ${className}`}>{children}<ArrowUpRight size={16} /></Link>;
}
